"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { createSupabaseBrowserClient } from "@/lib/supabase/client";

/**
 * Detects when the Supabase JS client auto-authenticates an invited user
 * from a hash fragment on /login (e.g. /login#access_token=...&type=invite).
 *
 * When this happens, checks if:
 *  1. The session's user_metadata contains an invite_token
 *  2. The user has no active org membership (no profile_orgs rows)
 *
 * If both conditions hold, redirects to /invite/{invite_token} so the user
 * can complete the invite acceptance flow instead of being silently dumped
 * to /dashboard.
 */
export function useInviteRedirect(): void {
  const router = useRouter();
  const handledRef = useRef(false);

  useEffect(() => {
    const supabase = createSupabaseBrowserClient();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (handledRef.current) return;
      if (event !== "SIGNED_IN" || !session) return;

      const inviteToken = session.user.user_metadata?.invite_token as
        | string
        | undefined;

      if (!inviteToken) return;

      // Check whether this user already has an org membership.
      // If they do, the normal /dashboard redirect is correct.
      const { data: membershipRows } = await supabase
        .from("profile_orgs")
        .select("org_id")
        .eq("profile_id", session.user.id);

      if (membershipRows && membershipRows.length > 0) return;

      // No org membership + invite_token present: redirect to invite flow
      handledRef.current = true;
      router.replace(`/invite/${inviteToken}`);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [router]);
}
